import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
  ApprovalProcessLogRow,
  SendForApprovalRequest,
  SendForApprovalResponse
} from './approval.model';

/**
 * Reads the Bordereau review/approval process log from the API.
 *
 * Response keys are lowercased so component bindings never depend on the API's
 * exact camelCase, mirroring {@link ReportDataService}.
 */
@Injectable({ providedIn: 'root' })
export class ApprovalService {
  /** apiBaseUrl without any trailing slash. */
  private readonly base = environment.apiBaseUrl.replace(/\/+$/, '');

  constructor(private readonly http: HttpClient) {}

  /**
   * Loads every process-log row that has been reviewed (isReviewed = 1),
   * newest reporting period first. The isReviewed filter and the ordering are
   * both applied server-side (ReviewApprovalProcessRepository).
   */
  getReviewedProcessLogs(): Observable<ApprovalProcessLogRow[]> {
    const url = `${this.base}/ReviewApprovalProcess/GetReviewedProcessLogs`;
    return this.http
      .get<unknown[]>(url)
      .pipe(map((rows) => (rows ?? []).map((row) => normaliseRow(row))));
  }

  /**
   * Emails the department's approvers that its reports are ready for approval.
   * The links in the message are built server-side from configuration, so the
   * caller only says which department and who asked.
   */
  sendForApproval(request: SendForApprovalRequest): Observable<SendForApprovalResponse> {
    const url = `${this.base}/ReviewApprovalProcess/SendForApproval`;
    return this.http.post<SendForApprovalResponse>(url, request);
  }
}

/** Maps a raw API row to an {@link ApprovalProcessLogRow}, lowercasing every top-level key. */
function normaliseRow(row: unknown): ApprovalProcessLogRow {
  const out: Record<string, unknown> = {};
  if (row && typeof row === 'object') {
    for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
      out[key.toLowerCase()] = value;
    }
  }
  return {
    reviewid: (out['reviewid'] as number) ?? null,
    processname: (out['processname'] as string) ?? null,
    department: (out['department'] as string) ?? null,
    reportingperiod: (out['reportingperiod'] as string) ?? null,
    isreviewed: (out['isreviewed'] as boolean) ?? null,
    reviewdate: (out['reviewdate'] as string) ?? null,
    reviewedby: (out['reviewedby'] as string) ?? null,
    isapproved: (out['isapproved'] as boolean) ?? null,
    approveddate: (out['approveddate'] as string) ?? null,
    approvedby: (out['approvedby'] as string) ?? null,
    isshared: (out['isshared'] as boolean) ?? null,
    shareddate: (out['shareddate'] as string) ?? null,
    sharedby: (out['sharedby'] as string) ?? null,
    rundate: (out['rundate'] as string) ?? null
  };
}
